/**
 * usePricing Hook - React hook for accessing dynamic pricing configuration
 *
 * Fetches pricing data from Firestore siteConfig/pricing document.
 * Provides a clean interface for components to display current pricing.
 *
 * Usage:
 *   const { pricing, loading, error } = usePricing();
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <ErrorMessage />;
 *
 *   return <div>{pricing.pro.price}</div>;
 */

import { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Default pricing fallback if Firestore is unavailable
const DEFAULT_PRICING = {
  free: {
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: [
      'Watch 10-30s ad before meetings',
      'Up to 4 participants',
      '720p video quality',
      '720p screen sharing',
      'P2P encrypted chat'
    ]
  },
  pro: {
    name: 'Pro',
    price: '$15',
    period: 'per user/month',
    stripePriceId: 'price_1QaREQRrHhWBmwBE6FGLG3Qr',
    features: [
      'No ads - instant meetings',
      'Up to 12 participants',
      '1080p video quality',
      '1080p screen sharing',
      'Unlimited recordings',
      'Meeting notes & archive'
    ]
  },
  team: {
    name: 'Team',
    price: '$20',
    period: 'per user/month',
    description: 'For small businesses',
    minSeats: 5,
    stripePriceId: '',
    features: [
      'Unlimited participants',
      'Team dashboard & admin controls',
      'AI meeting summaries',
      'Shared team library',
      'Custom branding & URLs',
      'Breakout rooms',
      'Advanced security controls'
    ]
  }
};

export const usePricing = () => {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        setLoading(true);
        setError(null);

        const configRef = doc(db, 'siteConfig', 'pricing');
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
          setPricing(configSnap.data());
        } else {
          // Use default pricing if not configured
          setPricing(DEFAULT_PRICING);
        }
      } catch (err) {
        console.error('Error loading pricing:', err);
        setError(err.message);
        // Fallback to default pricing on error
        setPricing(DEFAULT_PRICING);
      } finally {
        setLoading(false);
      }
    };

    loadPricing();
  }, []);

  /**
   * Get pricing for a specific tier
   * @param {string} tier - 'free', 'pro', or 'team'
   * @returns {object} Tier pricing configuration
   */
  const getTierPricing = (tier) => {
    return pricing[tier] || null;
  };

  /**
   * Get Stripe price ID for a tier
   * @param {string} tier - 'pro' or 'team'
   * @returns {string} Stripe price ID
   */
  const getStripePriceId = (tier) => {
    return pricing[tier]?.stripePriceId || '';
  };

  return {
    pricing,
    loading,
    error,
    getTierPricing,
    getStripePriceId,

    // Individual tier accessors for convenience
    freePricing: pricing.free,
    proPricing: pricing.pro,
    teamPricing: pricing.team,
  };
};

export default usePricing;
